import React, {useEffect, useRef, useState} from 'react';
import './PersonalChat.scss';
import {delete_chat_by_id, get_chat_messages, send_message} from "../../api/chat_api";
import {isAlreadyToasted} from "../../api/instance";
import {useCurrentChatStore} from "../../store";
import Message from "../../components/ui/Message/Message";
import TextArea from "../../components/ui/TextArea/TextArea";
import {Trash2, Bot, ArrowLeft, MessagesSquare} from "lucide-react";
import ConfirmWindow from "../../components/layout/ConfirmWindow/ConfirmWindow";
import {useLoaderStore} from "../../store/loader/loader.store";
import {toast} from "react-toastify";
import {useProfileStore} from "../../store/profile/pofile.store";
import {auth_get_profile} from "../../api/auth_api";
import AiDisclaimer from "../../components/layout/AiDisclaimer/AiDisclaimer";
import {AI_DISCLAIMER_STORAGE_KEY} from "../../constants/legal";

const PersonalChat = ({chatData}: any) => {

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const [chatLoader, setChatLoader] = useState(false);
    const [text, setText] = useState('');
    const [deleteModal, setDeleteModal] = useState(false);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(
        () => localStorage.getItem(AI_DISCLAIMER_STORAGE_KEY) === 'true'
    );
    const {showLoader, hideLoader} = useLoaderStore();
    const {updateUser, updateUsage, updateChatPreview, user} = useProfileStore();

    const {currentChat, resetChat} = useCurrentChatStore();
    // Message history is loaded per chat from the server, newest page first
    const [messages, setMessages] = useState<any[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const botId = chatData?.bot_id;

    useEffect(() => {
        setMessages([]);
        setHasMore(false);
        if (!botId) return;
        let cancelled = false;
        setHistoryLoading(true);
        get_chat_messages(botId).then((res) => {
            if (cancelled) return;
            setMessages(res.data.messages);
            setHasMore(res.data.has_more);
        }).catch(() => {
            if (!cancelled) toast('Could not load messages', {type: "error"});
        }).finally(() => {
            if (!cancelled) setHistoryLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [botId]);

    const loadOlderMessages = async () => {
        if (!messages.length || historyLoading) return;
        setHistoryLoading(true);
        try {
            const res = await get_chat_messages(botId, messages[0].created_at);
            setMessages((prev) => [...res.data.messages, ...prev]);
            setHasMore(res.data.has_more);
        } catch (error) {
            toast('Could not load messages', {type: "error"});
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({
            behavior: "smooth", // или "auto"
        });
    }, [messages.length, chatLoader]);

    const sendMessage = async (e: any) => {
        e.preventDefault();
        const message = text.trim();
        if (!message.length || chatLoader) return;

        setChatLoader(true);

        const newMessage = {
            bot_id: chatData.bot_id,
            message,
            contact_name: chatData.person_to_copy,
            message_sender: 'user',
            message_sender_name: user?.name,
            date: new Date().toISOString(),
            // The server decides which replies come back as voice messages
            message_type: 'text',
        };

        setText('');

        // Сначала обновляем локально
        const pendingMessage = {...newMessage, pending: true};
        setMessages((prev) => [...prev, pendingMessage]);

        try {
            // Отправляем на сервер
            const result = await send_message(newMessage);
            const {user_message, bot_message, voice_limit_reached, usage} = result.data;

            // Replace the local copy with the stored messages
            setMessages((prev) => [...prev.filter((m) => m !== pendingMessage), user_message, bot_message]);

            if (voice_limit_reached) {
                toast('Voice replies for this month are used up, so replies come as text. Upgrade your plan in Settings for more.', {type: "info"});
            }

            // Обновляем превью последнего сообщения в списке чатов
            updateChatPreview(chatData.bot_id, bot_message);
            // The reply carries the new monthly usage, so the meters in Settings stay current
            if (usage) updateUsage(usage);
        } catch (error: any) {
            setMessages((prev) => prev.filter((m) => m !== pendingMessage));
            // Plan limit errors already show a toast from the axios interceptor
            if (!isAlreadyToasted(error)) {
                toast('Message was not sent', {type: "error"});
            }
            setText(message);
        } finally {
            setChatLoader(false);
        }
    };

    const acceptDisclaimer = () => {
        localStorage.setItem(AI_DISCLAIMER_STORAGE_KEY, 'true');
        setDisclaimerAccepted(true);
    };

    const deleteChat = async (e: any) => {
        showLoader('Deleting all chat data...');
        try {
            await delete_chat_by_id(currentChat.bot_id);
            toast("Chat deleted!", {type: "success"});

            const profile = await auth_get_profile();
            updateUser(profile.data);
        } catch (error) {
            toast('Error', {type: "error"});
        } finally {
            hideLoader();
            resetChat();
            setDeleteModal(false);
        }
    };
    return (
        <div className={`PersonalChat ${currentChat ? 'PersonalChatOpen' : ''}`}>

            {
                chatData ? <>
                        {!disclaimerAccepted &&
                            <AiDisclaimer personName={chatData?.person_to_copy} onAccept={acceptDisclaimer}
                                          onDecline={() => resetChat()}/>}

                        {deleteModal &&
                            <ConfirmWindow confirmAction={deleteChat} closeMenu={() => setDeleteModal(false)}
                                           type={'confirm'} chatName={chatData?.chat_name}/>}

                        <div className="PersonalChat__botData">
                            <button type="button" className="icon-button PersonalChat__botData_back"
                                    aria-label="Back to chats" onClick={() => resetChat()}>
                                <ArrowLeft size={20}/>
                            </button>

                            <img src={chatData?.avatar} alt=""/>

                            <div className="PersonalChat__botData_name">
                                <h3>{chatData?.chat_name}</h3>
                                <span><i className="PersonalChat__onlineDot"/>AI clone of {chatData?.person_to_copy}</span>
                            </div>

                            <div className="PersonalChat__botData_delete">
                                <button type="button" className="icon-button icon-button--danger"
                                        aria-label="Delete chat" title="Delete chat"
                                        onClick={() => setDeleteModal(true)}>
                                    <Trash2 size={18}/>
                                </button>
                            </div>
                        </div>

                        <p className="PersonalChat__aiNotice" role="note">
                            <Bot size={14}/>
                            <span>
                                AI simulation of {chatData?.person_to_copy}, not the real person. Replies may be
                                inaccurate.
                            </span>
                        </p>

                        <div className="PersonalChat__chat">
                            <div className="PersonalChat__chat_messagesRow">
                                {
                                    hasMore &&
                                    <button className="PersonalChat__loadOlder" type="button"
                                            disabled={historyLoading} onClick={loadOlderMessages}>
                                        {historyLoading ? 'Loading...' : 'Load earlier messages'}
                                    </button>
                                }
                                {
                                    messages.length ?
                                        messages.map((el: any, idx: number) => (
                                            <Message key={el.id || `message${idx}`} data={el}/>
                                        ))
                                        : historyLoading ? <div className="PersonalChat__chat_messagesRow-empty">
                                                <span className="PersonalChat__spinner"/>
                                            </div>
                                            : <div className="PersonalChat__chat_messagesRow-empty">
                                                <img src={chatData?.avatar} alt=""/>
                                                <h4>Say hi to {chatData?.person_to_copy}</h4>
                                                <span>Send the first message. Replies come in their style and voice.</span>
                                            </div>
                                }
                                {
                                    chatLoader && <div className="PersonalChat__typing" aria-live="polite">
                                        <span className="PersonalChat__typingDots"><i/><i/><i/></span>
                                        {chatData.person_to_copy} is typing
                                    </div>
                                }
                                <div ref={messagesEndRef}/>
                            </div>
                            <TextArea text={text} setText={setText}
                                      sendMessage={sendMessage} chatLoader={chatLoader}
                                      placeholder={`Message ${chatData?.person_to_copy || ''}`}/>
                        </div>
                    </>
                    : <div className="PersonalChat__emptyChat">
                        <div className="icon-tile"><MessagesSquare size={22}/></div>
                        <h4>Select a chat</h4>
                        <span>Pick a conversation on the left to continue talking.</span>
                    </div>
            }

        </div>
    );
};

export default PersonalChat;
